export const M10_UNIT_12 = {
  id: "m10-u12-reported-questions",
  title: "Unit 12: What They Asked",
  topic: "Reported Questions + Raise Collocations",
  iconType: "help-circle",
  grammarImage: "/m10-u12-reported-questions.jpg",
  grammarText: "Unit focus: Reported questions — yes/no questions using if/whether, and WH- questions using the correct word order (subject + verb, never inverted). Combined with seven collocations using 'raise', each set tests grammar structure and vocabulary simultaneously.",

  sets: [

    // ===================================================================
    // SET 1: YES/NO REPORTED QUESTIONS — IF/WHETHER (B1+)
    // ===================================================================
    {
      id: "m10-u12-set1",
      title: "Set 1: Yes or No?",
      context: `*Karachi, Monday morning — Zara's campaign launch*

Zara is 29 and has spent three years organising climate campaigns across South Asia.
Today she is launching her biggest project yet — a global awareness drive
that will need thousands of volunteers and significant funding.
Tom had flown in from London to cover the story.
He had a lot of questions. Most of them had yes or no answers — at first.

*Karachi, lunes por la mañana — Lanzamiento de la campaña de Zara*

Zara tiene 29 años y ha pasado tres años organizando campañas climáticas por toda Asia del Sur.
Hoy lanza su proyecto más ambicioso — una iniciativa global de concienciación
que necesitará miles de voluntarios y financiación significativa.
Tom había volado desde Londres para cubrir la historia.
Tenía muchas preguntas. La mayoría tenían respuesta de sí o no — al principio.`,

      questions: [
        {
          type: "match",
          q: "Match the yes/no reported question rules:",
          pairs: [
            { left: "if", right: "más común — 'She asked if they were ready.'" },
            { left: "whether", right: "más formal — 'She asked whether funding had been secured.'" },
            { left: "word order", right: "subject + verb (NO inversion): asked if she WAS — not if WAS she" }
          ],
          a: "completed",
          successTip: "Yes/No questions → IF or WHETHER. Both correct! 'She asked if...' (common) / 'She asked whether...' (formal). NEVER keep the question word order! 'Asked if she was' ✅ 'Asked if was she' ❌"
        },

        {
          type: "match",
          q: "Match the RAISE collocations:",
          pairs: [
            { left: "raise hands", right: "levantar las manos" },
            { left: "raise money", right: "recaudar dinero" },
            { left: "raise awareness", right: "crear conciencia / sensibilizar" }
          ],
          a: "completed",
          successTip: "Three core RAISE collocations! Raise hands (physical gesture), raise money (fundraising), raise awareness (education campaign). All three use RAISE — never 'collect awareness' or 'lift hands'! 🙌💰"
        },

        {
          type: "mc",
          q: "Tom: 'Are you planning to raise money through events?' → He asked ___ she ___ to raise money through events.",
          options: ["if / was planning", "if / is planning", "whether / planned"],
          a: "if / was planning",
          tip: "Yes/No → if. Is planning (Present Continuous) → was planning (Past Continuous). No inversion!",
          successTip: "IS PLANNING → WAS PLANNING (backshift). IF (yes/no opener). No question word order — it's a statement structure now! 🎯"
        },

        {
          type: "mc",
          q: "Tom: 'Have you raised awareness in other countries before?' → He asked ___ she ___ awareness in other countries before.",
          options: ["whether / had raised", "if / has raised", "whether / raised"],
          a: "whether / had raised",
          tip: "Whether = formal yes/no. Have raised (Present Perfect) → had raised (Past Perfect). Backshift!",
          successTip: "HAVE RAISED → HAD RAISED (Present Perfect → Past Perfect). WHETHER = formal version of if. Both correct — context and register decide! 📰"
        },

        {
          type: "mc",
          q: "Which reported question has the correct word order?",
          options: [
            "He asked if the campaign was ready to launch.",
            "He asked if was the campaign ready to launch.",
            "He asked if ready was the campaign to launch."
          ],
          a: "He asked if the campaign was ready to launch.",
          tip: "Subject (the campaign) + verb (was) — never invert in reported questions!",
          successTip: "Error detection! Options 2 and 3 keep the question word order — NEVER do this in reported speech! 'If WAS the campaign' ❌ 'If the campaign WAS' ✅ Subject before verb! 📌"
        },

        {
          type: "mc",
          q: "Zara asked the volunteers to raise their hands ___ they ___ committed to the full three months.",
          options: ["if / were", "whether / are", "if / had been"],
          a: "if / were",
          tip: "If = yes/no question embedded in instruction. Are → were (backshift).",
          successTip: "RAISE HANDS in context! She asked them to raise their hands IF they were committed. IF introduces the yes/no condition. Are → were (backshift). Two elements! 🙌"
        },

        {
          type: "mc",
          q: "Tom: 'Will the campaign raise enough money?' → He wondered ___ the campaign ___ raise enough money.",
          options: ["whether / would", "if / will", "whether / could"],
          a: "whether / would",
          tip: "Wondered = reporting verb for questions. Will → would (backshift). Whether = formal.",
          successTip: "WONDERED = a reporting verb for questions! Like 'asked' but more internal/reflective. WILL → WOULD (backshift). Whether = slightly more formal for written journalism! 📰"
        },

        {
          type: "mc",
          q: "Priya called Tom: 'Did Zara say if they ___ money through social media?' Tom replied: 'She said they ___.'",
          options: ["were raising / were", "are raising / are", "had raised / had"],
          a: "were raising / were",
          tip: "Reported within reported: are raising → were raising. Short answer keeps the same backshifted form.",
          successTip: "Reported speech within a conversation! Both speakers use the backshifted form (were raising). Consistency is key — once you backshift, stay in the same tense! 🔄"
        },

        {
          type: "order",
          q: "Reorder: 'Tom preguntó si la campaña había recaudado suficiente dinero para continuar.'",
          words: ["Tom", "asked", "if", "the", "campaign", "had", "raised", "enough", "money", "to", "continue."],
          a: "Tom asked if the campaign had raised enough money to continue.",
          extra: ["whether", "has", "raised"]
        },

        {
          type: "input",
          q: "Press briefing: 'Tom wanted to know ___ the team ___ ___ awareness globally or only regionally.' (if + plan — past continuous backshift)",
          a: "if / was planning / to raise",
          alts: ["whether / was planning / to raise", "If / was planning / to raise"],
          successTip: "IF/WHETHER + WAS PLANNING (backshift) + TO RAISE AWARENESS (collocation). Three elements in one reported question. The structure is complete! ✅🎯"
        }
      ]
    },

    // ===================================================================
    // SET 2: WH- QUESTIONS — WHAT / WHERE / WHEN (B1+)
    // ===================================================================
    {
      id: "m10-u12-set2",
      title: "Set 2: What, Where and When",
      context: `*Karachi, Monday afternoon — Tom's first full interview with Zara*

Tom had moved from yes/no questions to the harder ones.
The ones that started with what, where, and when.
These required more than a single answer — they required explanation.
Zara had answers for all of them.
Some of those answers raised questions of their own.

*Karachi, lunes por la tarde — Primera entrevista completa de Tom con Zara*

Tom había pasado de las preguntas de sí/no a las más difíciles.
Las que empezaban con qué, dónde y cuándo.
Estas requerían más que una sola respuesta — requerían explicación.
Zara tenía respuestas para todas.
Algunas de esas respuestas planteaban sus propias preguntas.`,

      questions: [
        {
          type: "match",
          q: "Match the WH- reported question rules:",
          pairs: [
            { left: "WH- word + subject + verb", right: "She asked WHAT the campaign WAS about. ✅" },
            { left: "NO inversion", right: "She asked WHAT WAS the campaign about. ❌" },
            { left: "backshift applies", right: "What IS it? → asked what IT WAS." }
          ],
          a: "completed",
          successTip: "WH- questions in reported speech = WH-word + subject + verb. NEVER invert! 'Asked what the plan was' ✅ 'Asked what was the plan' ❌ The inversion disappears in reported speech! 📌"
        },

        {
          type: "match",
          q: "Match the RAISE collocations in context:",
          pairs: [
            { left: "raise awareness", right: "Her campaign raised awareness of ocean pollution." },
            { left: "raise the question", right: "This raises the question of who is responsible." },
            { left: "raise eyebrows", right: "Her bold strategy raised eyebrows in the industry." }
          ],
          a: "completed",
          successTip: "RAISE AWARENESS (educate/inform), RAISE THE QUESTION (bring up an important issue), RAISE EYEBROWS (cause surprise or scepticism). Three very different metaphorical meanings! 👀❓💡"
        },

        {
          type: "mc",
          q: "Tom: 'What is the campaign raising awareness about?' → He asked what the campaign ___ awareness about.",
          options: ["was raising", "is raising", "raised"],
          a: "was raising",
          tip: "What + subject (the campaign) + verb (was raising). IS → WAS backshift. No inversion!",
          successTip: "WHAT + THE CAMPAIGN + WAS RAISING (no inversion!). IS RAISING → WAS RAISING (backshift). And the collocation 'raising awareness' stays intact inside the reported question! 💡"
        },

        {
          type: "mc",
          q: "Tom: 'Where are the fundraising events happening?' → He asked where the fundraising events ___.",
          options: ["were happening", "are happening", "happened"],
          a: "were happening",
          tip: "Where + subject + verb. Are happening → were happening (backshift).",
          successTip: "WHERE + SUBJECT + WERE HAPPENING. No 'where were happening the events' ❌ — the subject comes immediately after the WH- word! ✅"
        },

        {
          type: "mc",
          q: "Tom: 'When did this issue first raise fears in the scientific community?' → He asked when the issue ___ first ___ fears in the scientific community.",
          options: ["had / raised", "did / raise", "has / raised"],
          a: "had / raised",
          tip: "When + subject + verb. Did raise (Past Simple) → had raised (Past Perfect). Raise fears = collocation.",
          successTip: "DID RAISE → HAD RAISED (Past Simple → Past Perfect backshift). RAISE FEARS collocation stays intact. When + subject + Past Perfect = elegant reported question! ⏪"
        },

        {
          type: "mc",
          q: "Choose the correctly reported question:",
          options: [
            "She asked what the data showed about rising temperatures.",
            "She asked what showed the data about rising temperatures.",
            "She asked what did the data show about rising temperatures."
          ],
          a: "She asked what the data showed about rising temperatures.",
          tip: "What + subject (the data) + verb (showed). No auxiliary, no inversion.",
          successTip: "Error detection! Option 2 inverts (showed the data ❌). Option 3 keeps the auxiliary 'did' ❌ — auxiliaries disappear in reported questions! Only Option 1 is correct! 🔎"
        },

        {
          type: "mc",
          q: "Zara: 'What questions does this raise about government responsibility?' → She asked what questions this ___ about government responsibility.",
          options: ["raised", "raises", "had raised"],
          a: "raised",
          tip: "Does raise (Present Simple) → raised (Past Simple). What + subject (this) + raised.",
          successTip: "DOES RAISE → RAISED (backshift). RAISE THE QUESTION collocation — now inside a reported question. The collocation and the grammar work simultaneously! ❓✅"
        },

        {
          type: "mc",
          q: "Tom ___ where Zara ___ the most money and when the campaign ___ raising eyebrows internationally.",
          options: ["asked / had raised / had started", "wondered / raised / started", "asked / has raised / started"],
          a: "asked / had raised / had started",
          tip: "Two WH- reported questions. Had raised + had started = Past Perfect (backshifted). Asked = reporting verb.",
          successTip: "Two reported questions in one sentence! Both use Past Perfect (HAD RAISED, HAD STARTED) because the originals were Past Simple. Raise money + raise eyebrows both appear! 💰👀"
        },

        {
          type: "order",
          q: "Reorder: 'Tom preguntó dónde iba a tener lugar la campaña y qué conciencia pretendía crear.'",
          words: ["Tom", "asked", "where", "the", "campaign", "was", "going", "to", "take", "place", "and", "what", "awareness", "it", "aimed", "to", "raise."],
          a: "Tom asked where the campaign was going to take place and what awareness it aimed to raise.",
          extra: ["when", "had", "raising"]
        },

        {
          type: "input",
          q: "Priya's notes: 'Tom wanted to know ___ Zara ___ the question of funding so openly and ___ she ___ awareness in countries with limited internet access.' (why + raise / how + plan)",
          a: "why / had raised / how / planned",
          alts: ["Why / had raised / how / planned"],
          successTip: "Two WH- questions (why + how), two backshifts (had raised, planned), two collocations (raise the question + raise awareness). Four decisions — four correct! 🏆"
        }
      ]
    },

    // ===================================================================
    // SET 3: WH- QUESTIONS — WHO / WHY / HOW (B2-)
    // ===================================================================
    {
      id: "m10-u12-set3",
      title: "Set 3: Who, Why and How",
      context: `*Karachi, Tuesday — Tom interviews Zara's campaign team*

Tom had been speaking to the wider team.
Each person he spoke to raised a different angle of the story.
Their answers surprised him — and some of their answers
raised eyebrows back in his London newsroom.
"Who, why, and how," his editor had told him, "are always the important questions."

*Karachi, martes — Tom entrevista al equipo de campaña de Zara*

Tom había estado hablando con el equipo más amplio.
Cada persona con la que hablaba planteaba un ángulo diferente de la historia.
Sus respuestas le sorprendieron — y algunas de sus respuestas
causaron asombro en su redacción de Londres.
"Quién, por qué y cómo," le había dicho su editor, "son siempre las preguntas importantes."`,

      questions: [
        {
          type: "match",
          q: "Match the WH- words to their reported question structures:",
          pairs: [
            { left: "Who funded this?", right: "She asked who had funded it." },
            { left: "Why did it raise fears?", right: "He asked why it had raised fears." },
            { left: "How did you raise eyebrows?", right: "They asked how she had raised eyebrows." }
          ],
          a: "completed",
          successTip: "WHO, WHY, HOW all follow the same rule: WH-word + subject + verb (no inversion). Past Simple in original → Past Perfect in reported. The pattern never changes! ⏪"
        },

        {
          type: "mc",
          q: "Tom: 'Who is funding the campaign?' → He asked who ___ the campaign.",
          options: ["was funding", "is funding", "funded"],
          a: "was funding",
          tip: "Who + verb (was funding). Is funding → was funding (backshift). Who acts as the subject here — no separate subject needed!",
          successTip: "WHO as subject! When WHO is the subject of the question, it goes straight to the verb: 'asked who WAS FUNDING'. No extra subject needed because WHO IS the subject! 🔍"
        },

        {
          type: "mc",
          q: "Tom: 'Why did the campaign raise fears among local officials?' → He asked why the campaign ___ fears among local officials.",
          options: ["had raised", "raised", "was raising"],
          a: "had raised",
          tip: "Why + subject + verb. Did raise (Past Simple) → had raised (Past Perfect). Raise fears = collocation.",
          successTip: "DID RAISE → HAD RAISED (Past Simple → Past Perfect). RAISE FEARS inside a reported WHY question. Two grammar layers and a collocation! ⚠️⏪"
        },

        {
          type: "mc",
          q: "Tom: 'How has the campaign raised eyebrows internationally?' → He asked how the campaign ___ eyebrows internationally.",
          options: ["had raised", "has raised", "raised"],
          a: "had raised",
          tip: "How + subject + verb. Has raised (Present Perfect) → had raised (Past Perfect). Raise eyebrows = collocation.",
          successTip: "HAS RAISED → HAD RAISED (Present Perfect → Past Perfect). RAISE EYEBROWS inside a HOW reported question. The collocation travels intact into reported speech! 👀"
        },

        {
          type: "mc",
          q: "Choose the correctly reported question:",
          options: [
            "He asked why the data had raised such serious fears.",
            "He asked why had the data raised such serious fears.",
            "He asked why did the data raise such serious fears."
          ],
          a: "He asked why the data had raised such serious fears.",
          tip: "Why + subject (the data) + verb (had raised). No inversion, no auxiliary did.",
          successTip: "Error detection! 'Why HAD the data' ❌ (inversion). 'Why DID the data raise' ❌ (kept auxiliary). 'Why the data HAD RAISED' ✅ — subject before verb, Past Perfect! 🔎"
        },

        {
          type: "mc",
          q: "The editor in London ___ Tom ___ the campaign ___ eyebrows and ___ it ___ done so.",
          options: ["asked / how / had raised / why / had", "wondered / why / raised / how / did", "asked / what / was raising / when / had"],
          a: "asked / how / had raised / why / had",
          tip: "Two WH- questions: how + why. Both Past Perfect (had raised, had). Asked = reporting verb.",
          successTip: "Two reported questions back to back! HOW the campaign HAD RAISED eyebrows + WHY it HAD done so. Both Past Perfect, both correct word order. The editor wanted both answers! 👀"
        },

        {
          type: "input",
          q: "Tom's report: 'His editor wanted to know ___ the campaign ___ fears among officials — and ___ those officials ___ done about it.' (why + raise / what + do — past perfect)",
          a: "why / had raised / what / had",
          alts: ["Why / had raised / what / had"],
          tip: "Why + had raised (Past Perfect). What + had done (Past Perfect). Both WH- reported questions."
        },

        {
          type: "mc",
          q: "Priya ___ Tom ___ the campaign ___ such strong reactions and ___ he ___ the most surprising element.",
          options: ["asked / why / had provoked / what / found", "wondered / how / raised / who / thought", "asked / why / had raised / what / had found"],
          a: "asked / why / had raised / what / had found",
          tip: "Why + had raised (Past Perfect). What + had found (Past Perfect). Both WH- questions reported correctly.",
          successTip: "WHY the campaign HAD RAISED strong reactions + WHAT he HAD FOUND most surprising. Both Past Perfect, both correct word order. Two reported questions, one clean sentence! 🎯"
        },

        {
          type: "order",
          q: "Reorder: 'El editor preguntó por qué la campaña había despertado tanto temor y cómo había causado tanto asombro al mismo tiempo.'",
          words: ["The", "editor", "asked", "why", "the", "campaign", "had", "raised", "so", "much", "fear", "and", "how", "it", "had", "raised", "so", "many", "eyebrows", "at", "the", "same", "time."],
          a: "The editor asked why the campaign had raised so much fear and how it had raised so many eyebrows at the same time.",
          extra: ["when", "did", "raised"]
        },

        {
          type: "input",
          q: "Final question: 'Tom's editor asked ___ Zara ___ the question of accountability so directly — and ___ no official ___ responded publicly yet.' (why + raise / why + not)",
          a: "why / had raised / why / had",
          alts: ["Why / had raised / why / had"],
          successTip: "WHY + HAD RAISED (raise the question collocation in a reported question!) + WHY + HAD NOT RESPONDED (negative Past Perfect). Raise the question — inside a reported question! Beautiful recursion! ❓✅"
        }
      ]
    },

    // ===================================================================
    // SET 4: YES/NO vs WH- CONTRAST (B2-)
    // ===================================================================
    {
      id: "m10-u12-set4",
      title: "Set 4: Which Type of Question?",
      context: `*Karachi, Tuesday afternoon — the campaign press conference*

Zara had called an open press conference.
Journalists from twelve countries had questions.
Some questions had yes/no answers. Others needed explanation.
Tom sat at the back, taking notes — not of the answers,
but of the questions, and how they were being reported across the room.

*Karachi, martes por la tarde — La rueda de prensa de la campaña*

Zara había convocado una rueda de prensa abierta.
Periodistas de doce países tenían preguntas.
Algunas preguntas tenían respuestas de sí/no. Otras necesitaban explicación.
Tom se sentó al fondo, tomando notas — no de las respuestas,
sino de las preguntas y de cómo se estaban reportando en la sala.`,

      questions: [
        {
          type: "match",
          q: "Match the question type to its reported opener:",
          pairs: [
            { left: "Yes/No question", right: "if / whether + subject + verb" },
            { left: "WH- question", right: "what / where / when / who / why / how + subject + verb" },
            { left: "Both types", right: "No inversion. No auxiliary. Subject before verb." }
          ],
          a: "completed",
          successTip: "The golden rule applies to BOTH types: subject before verb, no inversion, no auxiliary. The only difference is the opener: if/whether (yes/no) vs WH-word (open questions)! 📌"
        },

        {
          type: "mc",
          q: "A journalist: 'Are you planning to raise the roof at the closing event?' → She asked ___ Zara ___ to raise the roof at the closing event.",
          options: ["if / was planning", "what / was planning", "if / planned"],
          a: "if / was planning",
          tip: "Yes/No question (are you planning?) → if. Is planning → was planning (backshift).",
          successTip: "RAISE THE ROOF (make a big impression/celebration) inside a yes/no reported question. IF + WAS PLANNING (backshift). Yes/no questions don't have WH- words — so opener = if/whether! 🎉"
        },

        {
          type: "mc",
          q: "Another journalist: 'What will the closing event raise awareness about?' → She asked ___ the closing event ___ awareness about.",
          options: ["what / would raise", "if / would raise", "what / will raise"],
          a: "what / would raise",
          tip: "WH- question (what) → what. Will → would (backshift). No if/whether for WH- questions!",
          successTip: "WHAT = WH- word = no if/whether! WILL → WOULD (backshift). RAISE AWARENESS collocation intact. Never mix if/whether with a WH- word — they serve different question types! 🔄"
        },

        {
          type: "mc",
          q: "Choose the correct reported question: Original: 'Has the campaign raised enough money?'",
          options: [
            "She asked whether the campaign had raised enough money.",
            "She asked what the campaign had raised enough money.",
            "She asked whether had the campaign raised enough money."
          ],
          a: "She asked whether the campaign had raised enough money.",
          tip: "Has (yes/no) → whether. Had raised = backshift. No inversion!",
          successTip: "WHETHER (yes/no) + THE CAMPAIGN (subject) + HAD RAISED (verb — no inversion!). RAISE MONEY collocation. Three correct elements! 💰✅"
        },

        {
          type: "mc",
          q: "Original question: 'Why does this raise so many fears?' → Reported: She asked ___ this ___ so many fears.",
          options: ["why / raised", "if / raised", "why / had raised"],
          a: "why / raised",
          tip: "Why = WH- word. Does raise (Present Simple) → raised (Past Simple). Not Past Perfect here — 'does raise' is a general present.",
          successTip: "WHY (WH- word — no if!) + RAISED (Present Simple backshift — general truth stays as Past Simple, not Past Perfect). RAISE FEARS collocation. Three decisions! ⚠️"
        },

        {
          type: "mc",
          q: "Tom noted four questions from the press conference. Match each to its correct opener: 'Is it legal?' / 'When did it start?' / 'Who funded it?' / 'Has it raised money?'",
          options: [
            "if / when / who / whether",
            "whether / when / who / if",
            "if / what / who / whether"
          ],
          a: "if / when / who / whether",
          tip: "Is it legal? = yes/no = if. When did it start? = WH- = when. Who funded it? = WH- = who. Has it raised? = yes/no = whether.",
          successTip: "Four questions, four openers! IS (yes/no) = IF. WHEN (WH-) = WHEN. WHO (WH-) = WHO. HAS (yes/no) = WHETHER. The question type determines the opener every time! 🎯"
        },

        {
          type: "mc",
          q: "A journalist asked ___ the campaign ___ the roof at the finale and ___ this ___ fears about the political reaction.",
          options: ["whether / would raise / whether / might raise", "if / raised / what / raised", "whether / would raise / if / raised"],
          a: "whether / would raise / whether / might raise",
          tip: "Both yes/no questions = whether. Would raise + might raise = modal backshifts. Raise the roof + raise fears = two collocations!",
          successTip: "Two yes/no questions = both WHETHER. WOULD RAISE THE ROOF + MIGHT RAISE FEARS — two different collocations, two modal backshifts. One journalist, two concerns! 🎉⚠️"
        },

        {
          type: "order",
          q: "Reorder: 'Los periodistas preguntaron si la campaña recaudaría suficiente dinero y qué conciencia pretendía crear.'",
          words: ["The", "journalists", "asked", "whether", "the", "campaign", "would", "raise", "enough", "money", "and", "what", "awareness", "it", "aimed", "to", "raise."],
          a: "The journalists asked whether the campaign would raise enough money and what awareness it aimed to raise.",
          extra: ["if", "had", "raising"]
        },

        {
          type: "mc",
          q: "Tom's notes combined both question types: 'Journalists wanted to know ___ Zara ___ confident and ___ she ___ the campaign ___ succeed.'",
          options: ["whether / was / how / believed / would", "if / is / what / thinks / will", "whether / had been / why / thought / had"],
          a: "whether / was / how / believed / would",
          tip: "Is she confident? = yes/no = whether + was (backshift). How did she believe? = WH- = how + believed + would (backshift).",
          successTip: "WHETHER (yes/no, backshift was) + HOW (WH-, backshift believed + would). Mixed question types in one sentence — each with its correct opener and backshift! 🎯"
        },

        {
          type: "input",
          q: "Press conference summary: 'Every journalist had questions — ___ the campaign ___ eyebrows deliberately, and ___ it ___ the question of political accountability.' (whether + raise / how + raise — past perfect)",
          a: "whether / had raised / how / had raised",
          alts: ["Whether / had raised / how / had raised"],
          successTip: "WHETHER (yes/no) + HOW (WH-) — both with HAD RAISED (Past Perfect). RAISE EYEBROWS + RAISE THE QUESTION — two collocations in two reported questions. Complete mastery! 👀❓"
        }
      ]
    },

    // ===================================================================
    // SET 5: BACKSHIFT IN REPORTED QUESTIONS (B2-)
    // ===================================================================
    {
      id: "m10-u12-set5",
      title: "Set 5: Tenses Moving Back",
      context: `*Mumbai, Wednesday — Priya writes her article*

Priya had received Tom's notes from Karachi.
She was turning the raw questions into polished reported speech.
Every question needed its tense moved back — and every collocation
needed to land in exactly the right place.
"Backshift," she wrote at the top of her notes, "is not optional."

*Bombay, miércoles — Priya escribe su artículo*

Priya había recibido las notas de Tom desde Karachi.
Estaba convirtiendo las preguntas en bruto en estilo indirecto pulido.
Cada pregunta necesitaba retroceder su tiempo verbal — y cada colocación
debía encajar exactamente en el lugar correcto.
"El backshift," escribió en la parte superior de sus notas, "no es opcional."`,

      questions: [
        {
          type: "match",
          q: "Match the backshift rules for reported questions:",
          pairs: [
            { left: "Is / Are → Was / Were", right: "'Is it working?' → asked if it was working." },
            { left: "Will → Would", right: "'Will this raise fears?' → asked if this would raise fears." },
            { left: "Has/Have → Had", right: "'Has it raised money?' → asked if it had raised money." }
          ],
          a: "completed",
          successTip: "Same backshift rules as reported statements — but inside reported questions! Present → Past, Will → Would, Has → Had. The structure changes (subject + verb) but the tense rules stay identical! ⏪"
        },

        {
          type: "mc",
          q: "Tom: 'Is the campaign raising fears among politicians?' → Priya wrote: Tom asked if the campaign ___ fears among politicians.",
          options: ["was raising", "is raising", "had raised"],
          a: "was raising",
          tip: "Is raising (Present Continuous) → was raising (Past Continuous). If = yes/no.",
          successTip: "IS RAISING → WAS RAISING (Present Continuous → Past Continuous). IF (yes/no). RAISE FEARS collocation intact. Backshift moves the continuous tense back one step! ⏪"
        },

        {
          type: "mc",
          q: "Tom: 'Will the event raise the roof?' → Priya wrote: Tom wondered if the event ___ the roof.",
          options: ["would raise", "will raise", "was raising"],
          a: "would raise",
          tip: "Will → would (backshift). Wondered = reporting verb for questions. If = yes/no.",
          successTip: "WILL → WOULD (backshift). WONDERED (internal question reporting verb). RAISE THE ROOF collocation. Three elements, one clean reported question! 🎉"
        },

        {
          type: "mc",
          q: "Tom: 'How long has the campaign been raising awareness?' → Priya wrote: Tom asked how long the campaign ___ awareness.",
          options: ["had been raising", "has been raising", "was raising"],
          a: "had been raising",
          tip: "Has been raising (Present Perfect Continuous) → had been raising (Past Perfect Continuous). How long = WH- question.",
          successTip: "HAS BEEN RAISING → HAD BEEN RAISING (Present Perfect Continuous → Past Perfect Continuous). The longest backshift! HOW LONG = WH-. RAISING AWARENESS = collocation! 💡⏪"
        },

        {
          type: "mc",
          q: "Tom: 'Can the campaign raise enough money by December?' → Priya wrote: Tom asked ___ the campaign ___ enough money by December.",
          options: ["whether / could raise", "if / can raise", "whether / would raise"],
          a: "whether / could raise",
          tip: "Can → could (backshift). Whether = formal yes/no. December stays (specific date — no time shift needed for fixed dates).",
          successTip: "CAN → COULD (modal backshift). WHETHER (formal yes/no). Fixed dates like 'December' don't shift — only relative time words (tomorrow, now, today) shift! 📅"
        },

        {
          type: "mc",
          q: "Choose the sentence with the correct backshift:",
          options: [
            "Priya asked what question the data had raised about the policy.",
            "Priya asked what question the data has raised about the policy.",
            "Priya asked what question did the data raise about the policy."
          ],
          a: "Priya asked what question the data had raised about the policy.",
          tip: "Has raised → had raised (backshift). What + subject + verb (no inversion). Raise the question = collocation.",
          successTip: "Error detection! HAS RAISED ❌ (no backshift). DID RAISE ❌ (auxiliary + inversion). HAD RAISED ✅ (correct backshift). RAISE THE QUESTION collocation intact! ❓"
        },

        {
          type: "mc",
          q: "Tom had asked three questions. Priya reported: He asked ___ the campaign ___ awareness effectively, ___ it ___ the question of accountability, and ___ the finale ___ the roof.",
          options: ["whether / was raising / whether / raised / whether / would raise", "if / had raised / if / had raised / if / would raise", "whether / was raising / if / had raised / whether / would raise"],
          a: "whether / was raising / if / had raised / whether / would raise",
          tip: "Three yes/no questions. Backshifts: is raising→was raising, raised→had raised, will raise→would raise.",
          successTip: "Three reported questions, three backshifts, three collocations! WAS RAISING AWARENESS + HAD RAISED THE QUESTION + WOULD RAISE THE ROOF. The full raise set in one sentence! 🏆"
        },

        {
          type: "order",
          q: "Reorder: 'Priya preguntó si la campaña había despertado temores entre los políticos y cuánto dinero había recaudado hasta la fecha.'",
          words: ["Priya", "asked", "if", "the", "campaign", "had", "raised", "fears", "among", "politicians", "and", "how", "much", "money", "it", "had", "raised", "to", "date."],
          a: "Priya asked if the campaign had raised fears among politicians and how much money it had raised to date.",
          extra: ["whether", "was", "raising"]
        },

        {
          type: "mc",
          q: "Priya's editor: 'Why didn't Tom ask whether this was raising eyebrows at government level?' → Priya's editor asked ___ Tom ___ asked ___ this ___ eyebrows at government level.",
          options: ["why / hadn't / whether / was raising", "why / didn't / if / raised", "how / had / whether / had raised"],
          a: "why / hadn't / whether / was raising",
          tip: "Why (WH-) + hadn't asked (Past Perfect negative). Whether = yes/no embedded question. Was raising = backshifted continuous.",
          successTip: "Reported question CONTAINING another reported question! WHY (WH-) + HADN'T ASKED (Past Perfect) + WHETHER (yes/no) + WAS RAISING (backshift). RAISE EYEBROWS inside! 👀🔄"
        },

        {
          type: "input",
          q: "Priya's final note: 'The most important question nobody ___ ___ was ___ the campaign ___ the question of who ___ ultimately responsible.' (ask + Past Perfect / whether + raise / be)",
          a: "had asked / whether / had raised / was",
          alts: ["Had asked / whether / had raised / was"],
          successTip: "HAD ASKED (Past Perfect — nobody asked it before) + WHETHER (yes/no) + HAD RAISED (backshift) + WAS (backshift). RAISE THE QUESTION inside a reported question. The most important unreported question! ❓🏆"
        }
      ]
    },

    // ===================================================================
    // SET 6: WORD ORDER — THE CRITICAL RULE (B2)
    // ===================================================================
    {
      id: "m10-u12-set6",
      title: "Set 6: The Order That Changes Everything",
      context: `*London, Thursday — Tom's editorial meeting*

Tom had returned to London with hours of material.
Now he had to present it to his editorial team.
His editor, sharp and experienced, kept stopping him.
"That's not how you report a question, Tom."
Word order, Tom was learning, was not a detail. It was everything.

*Londres, jueves — Reunión editorial de Tom*

Tom había regresado a Londres con horas de material.
Ahora tenía que presentarlo a su equipo editorial.
Su editora, perspicaz y experimentada, no paraba de interrumpirle.
"Así no se reporta una pregunta, Tom."
El orden de las palabras, Tom iba aprendiendo, no era un detalle. Era todo.`,

      questions: [
        {
          type: "match",
          q: "Match the word order rules with examples:",
          pairs: [
            { left: "CORRECT", right: "She asked where the money WAS coming from." },
            { left: "INCORRECT — inversion", right: "She asked where WAS the money coming from." },
            { left: "INCORRECT — auxiliary", right: "She asked where DID the money come from." }
          ],
          a: "completed",
          successTip: "Two common errors: keeping the inversion (where WAS the money ❌) or keeping the auxiliary (where DID it come ❌). Both disappear in reported questions! Subject + verb only! 📌"
        },

        {
          type: "mc",
          q: "Tom's first version: 'She asked where was the money being raised.' His editor's correction:",
          options: [
            "She asked where the money was being raised.",
            "She asked where the money is being raised.",
            "She asked where raised the money was."
          ],
          a: "She asked where the money was being raised.",
          tip: "Remove the inversion: where + subject (the money) + verb (was being raised).",
          successTip: "Tom's error: WHERE WAS THE MONEY (inversion kept ❌). Editor's fix: WHERE THE MONEY WAS (subject before verb ✅). The inversion disappears in reported questions! 📌"
        },

        {
          type: "mc",
          q: "Tom: 'She asked how did the campaign raise eyebrows.' Editor's correction:",
          options: [
            "She asked how the campaign had raised eyebrows.",
            "She asked how had the campaign raised eyebrows.",
            "She asked how the campaign raises eyebrows."
          ],
          a: "She asked how the campaign had raised eyebrows.",
          tip: "Remove DID (auxiliary disappears). Add backshift: raise → had raised. How + subject + verb.",
          successTip: "Two errors fixed! DID removed (no auxiliary) + HAD RAISED (backshift added). RAISE EYEBROWS collocation intact. The editor was right on both counts! 👀✅"
        },

        {
          type: "mc",
          q: "Which version correctly reports: 'Will this raise fears?'",
          options: [
            "She asked whether this would raise fears.",
            "She asked whether would this raise fears.",
            "She asked whether this will raise fears."
          ],
          a: "She asked whether this would raise fears.",
          tip: "Whether (yes/no). This (subject) + would raise (verb — no inversion). Will → would (backshift).",
          successTip: "WHETHER + THIS (subject) + WOULD RAISE (verb). No inversion after WHETHER. WILL → WOULD (backshift). RAISE FEARS collocation. Three correct elements! ⚠️✅"
        },

        {
          type: "mc",
          q: "Tom wrote four reported questions. Which ONE is correct?",
          options: [
            "She asked what the campaign aimed to raise awareness about.",
            "She asked what aimed the campaign to raise awareness about.",
            "She asked what did the campaign aim to raise awareness about."
          ],
          a: "She asked what the campaign aimed to raise awareness about.",
          tip: "What + subject (the campaign) + verb (aimed). No inversion, no auxiliary.",
          successTip: "WHAT + THE CAMPAIGN (subject) + AIMED (verb). No inversion ❌, no DID ❌. RAISE AWARENESS collocation at the end. Option 1 is the only correct version! 💡✅"
        },

        {
          type: "mc",
          q: "Tom's draft: 'The editor asked ___ the story ___ eyebrows in the right places and ___ it ___ the question of accountability clearly.'",
          options: ["whether / was raising / whether / raised", "if / raised / what / raised", "whether / would raise / whether / raised"],
          a: "whether / was raising / whether / raised",
          tip: "Two yes/no questions = both whether. Is raising → was raising (backshift). Raises → raised (backshift).",
          successTip: "Two WHETHER questions (yes/no). WAS RAISING EYEBROWS (backshift + collocation) + RAISED THE QUESTION (backshift + collocation). Two collocations, correct word order, correct backshift! 👀❓"
        },

        {
          type: "mc",
          q: "Find the word order error: 'His editor wondered why had Zara raised the question of funding so publicly.'",
          options: [
            "The error is 'why had Zara' — should be 'why Zara had'.",
            "The error is 'raised the question' — should be 'risen the question'.",
            "The error is 'wondered' — should be 'asked'."
          ],
          a: "The error is 'why had Zara' — should be 'why Zara had'.",
          tip: "Why + subject (Zara) + verb (had raised). Never invert after the WH- word!",
          successTip: "WHY HAD ZARA ❌ (inversion) → WHY ZARA HAD ✅ (subject before verb). RAISE THE QUESTION collocation is correct — it never changes! WONDERED is also correct! 🔎"
        },

        {
          type: "order",
          q: "Reorder (correct the word order): 'El editor preguntó cómo había recaudado la campaña tanto dinero en tan poco tiempo.'",
          words: ["The", "editor", "asked", "how", "the", "campaign", "had", "raised", "so", "much", "money", "in", "such", "a", "short", "time."],
          a: "The editor asked how the campaign had raised so much money in such a short time.",
          extra: ["had raised", "raised had", "did raise"]
        },

        {
          type: "mc",
          q: "Tom rewrote all his reported questions. His editor said: 'Now ___ every question ___ the right structure — subject before verb, no inversion, correct backshift.'",
          options: ["every / has", "all / had", "each / had"],
          a: "every / has",
          tip: "Now = present result. Every question has = present perfect structure. General present statement.",
          successTip: "Praise from the editor! 'Every question HAS the right structure' — present state. Tom fixed all his word order errors. Subject before verb, no inversion, correct backshift. Done! ✅📰"
        },

        {
          type: "input",
          q: "Tom's revised sentence: 'The editor asked ___ the closing event ___ the roof and ___ the campaign ___ raised enough awareness to make a real difference.' (whether + raise / whether + have — past perfect)",
          a: "whether / would raise / whether / had raised",
          alts: ["Whether / would raise / whether / had raised"],
          successTip: "WHETHER + WOULD RAISE THE ROOF (yes/no, will→would) + WHETHER + HAD RAISED AWARENESS (yes/no, has→had). Two collocations, correct word order, full backshift. The editor is satisfied! 🎉💡✅"
        }
      ]
    },

    // ===================================================================
    // SET 7: REPORTED QUESTIONS + REPORTING VERBS (B2)
    // ===================================================================
    {
      id: "m10-u12-set7",
      title: "Set 7: More Than Just 'Asked'",
      context: `*Karachi, Friday — Zara's team debrief*

The press conference was over. The team had gathered to review it.
Everyone had questions about the questions.
Zara noticed that not all questions were asked the same way —
some journalists had demanded, others had wondered,
and a few had simply enquired, very politely.
The verb, she realised, told you as much as the question itself.

*Karachi, viernes — Reunión de revisión del equipo de Zara*

La rueda de prensa había terminado. El equipo se había reunido para revisarla.
Todos tenían preguntas sobre las preguntas.
Zara notó que no todas las preguntas se habían formulado de la misma manera —
algunos periodistas habían exigido, otros se habían preguntado,
y unos pocos simplemente habían preguntado, muy educadamente.
El verbo, se dio cuenta, te decía tanto como la propia pregunta.`,

      questions: [
        {
          type: "match",
          q: "Match the reporting verbs for questions to their meaning:",
          pairs: [
            { left: "asked", right: "preguntó (neutro — el más común)" },
            { left: "wondered", right: "se preguntó (interno / reflexivo)" },
            { left: "enquired", right: "preguntó (formal / educado)" },
            { left: "demanded", right: "exigió saber (urgente / agresivo)" }
          ],
          a: "completed",
          successTip: "Four reporting verbs for questions! ASKED (neutral), WONDERED (internal reflection), ENQUIRED (formal/polite), DEMANDED (aggressive/urgent). The verb reveals the questioner's tone! 🎭"
        },

        {
          type: "mc",
          q: "A polite journalist raised her hand and gently asked about funding. Zara's notes: 'She ___ whether the campaign ___ enough money for the next phase.'",
          options: ["enquired / had raised", "demanded / had raised", "wondered / raised"],
          a: "enquired / had raised",
          tip: "Polite, gentle = enquired. Whether = yes/no. Has raised → had raised (backshift).",
          successTip: "ENQUIRED = the polite/formal version of asked. Perfect for written journalism and formal contexts. HAD RAISED (backshift from has raised). RAISE MONEY collocation! 💰"
        },

        {
          type: "mc",
          q: "An aggressive journalist stood up and challenged Zara directly. Zara's notes: 'He ___ why the campaign ___ so many fears instead of solutions.'",
          options: ["demanded to know / had raised", "enquired / raised", "wondered / was raising"],
          a: "demanded to know / had raised",
          tip: "Aggressive challenge = demanded to know. Why + subject + verb. Had raised = Past Perfect.",
          successTip: "DEMANDED TO KNOW = aggressive, urgent version of asked. Note: demanded to know (not just demanded). WHY + HAD RAISED. RAISE FEARS collocation. The tone of the question is captured! ⚠️"
        },

        {
          type: "mc",
          q: "After the press conference, Tom sat alone and ___ to himself ___ the campaign ___ raised enough awareness to actually change anything.",
          options: ["wondered / whether / had", "asked / if / has", "enquired / whether / had"],
          a: "wondered / whether / had",
          tip: "Internal reflection = wondered. Whether = yes/no (formal). Had = Past Perfect (backshift).",
          successTip: "WONDERED (internal, reflective — to himself!) + WHETHER (yes/no) + HAD RAISED (Past Perfect). When journalists reflect alone, they WONDER — they don't ask or enquire! 🤔"
        },

        {
          type: "mc",
          q: "Zara's colleague raised her hand and ___ how many people ___ raised their hands at the opening event to show commitment.",
          options: ["asked / had", "wondered / have", "enquired / raised"],
          a: "asked / had",
          tip: "Neutral team question = asked. How many + subject + verb. Had raised = Past Perfect.",
          successTip: "ASKED (neutral team context) + HOW MANY + HAD RAISED HANDS (Past Perfect + raise hands collocation). RAISE HANDS inside a reported question! 🙌"
        },

        {
          type: "mc",
          q: "A journalist from a major outlet ___ directly ___ the campaign ___ the roof or merely ___ eyebrows with its tactics.",
          options: ["demanded to know / whether / had raised / raised", "asked / whether / would raise / had raised", "enquired / if / raised / would raise"],
          a: "demanded to know / whether / had raised / raised",
          tip: "Direct challenge = demanded to know. Whether = yes/no. Had raised the roof + raised eyebrows = both collocations, different tenses.",
          successTip: "DEMANDED TO KNOW (aggressive) + WHETHER (yes/no) + HAD RAISED THE ROOF + RAISED EYEBROWS. Two collocations, one question, maximum impact! 🎉👀"
        },

        {
          type: "mc",
          q: "Choose the sentence that uses the most appropriate reporting verb: Context — a formal written survey about the campaign.",
          options: [
            "Respondents enquired whether the campaign had raised awareness effectively.",
            "Respondents demanded whether the campaign had raised awareness effectively.",
            "Respondents wondered out loud whether the campaign had raised awareness effectively."
          ],
          a: "Respondents enquired whether the campaign had raised awareness effectively.",
          tip: "Written survey = formal = enquired. Demanded = too aggressive. Wondered out loud = contradictory (wondered = internal).",
          successTip: "Context determines verb! Survey = formal = ENQUIRED. DEMANDED = too aggressive for a survey. WONDERED OUT LOUD = contradictory (wondering is internal!). ENQUIRED + WHETHER + HAD RAISED AWARENESS! 💼💡"
        },

        {
          type: "order",
          q: "Reorder: 'Los periodistas exigieron saber por qué la campaña había despertado tanto temor y un periodista se preguntó si realmente había creado suficiente conciencia.'",
          words: ["The", "journalists", "demanded", "to", "know", "why", "the", "campaign", "had", "raised", "so", "much", "fear,", "and", "one", "journalist", "wondered", "whether", "it", "had", "really", "raised", "enough", "awareness."],
          a: "The journalists demanded to know why the campaign had raised so much fear, and one journalist wondered whether it had really raised enough awareness.",
          extra: ["asked", "enquired", "raising"]
        },

        {
          type: "mc",
          q: "Zara summarised the press conference: 'Some journalists ___ politely, some ___ aggressively, and one simply ___ herself ___ any of it ___ the change she hoped for.'",
          options: ["enquired / demanded / wondered / whether / would bring", "asked / ordered / wondered / if / will bring", "enquired / demanded / asked / whether / brought"],
          a: "enquired / demanded / wondered / whether / would bring",
          tip: "Polite = enquired. Aggressive = demanded. Internal reflection = wondered. Whether = yes/no. Will → would (backshift).",
          successTip: "Three reporting verbs for three types of questioners: ENQUIRED (polite) + DEMANDED (aggressive) + WONDERED (internal). Each one tells you exactly who that journalist was! 🎭"
        },

        {
          type: "input",
          q: "Final debrief note: 'The most thoughtful question came from a journalist who quietly ___ ___ the campaign ___ raised the right questions — or merely the right amount of money.' (wonder + whether + raise — past perfect)",
          a: "wondered / whether / had raised",
          alts: ["Wondered / whether / had raised"],
          successTip: "WONDERED (thoughtful, quiet reflection) + WHETHER (yes/no) + HAD RAISED (Past Perfect). RAISE THE RIGHT QUESTIONS — a beautiful metaphorical extension of raise the question! ❓🏆"
        }
      ]
    },

    // ===================================================================
    // SET 8: NEGATIVE REPORTED QUESTIONS (B2)
    // ===================================================================
    {
      id: "m10-u12-set8",
      title: "Set 8: Why Didn't You?",
      context: `*Karachi, Saturday — a difficult political interview*

The campaign had attracted attention from a government minister.
He had questions — but not friendly ones.
His questions all seemed to start with "Why didn't you..." and "Why haven't you..."
Zara had answers. But she also had her own questions about his questions.
"Negative questions," Tom wrote in his notes, "always reveal the most."

*Karachi, sábado — Una difícil entrevista política*

La campaña había atraído la atención de un ministro del gobierno.
Tenía preguntas — pero no amistosas.
Sus preguntas parecían empezar todas con "¿Por qué no...?" y "¿Por qué no has...?"
Zara tenía respuestas. Pero también tenía sus propias preguntas sobre sus preguntas.
"Las preguntas negativas," escribió Tom en sus notas, "siempre revelan más."`,

      questions: [
        {
          type: "match",
          q: "Match the negative reported question structures:",
          pairs: [
            { left: "Why didn't you raise awareness earlier?", right: "He asked why she hadn't raised awareness earlier." },
            { left: "Haven't you raised enough money?", right: "He asked whether she hadn't raised enough money." },
            { left: "Why isn't this raising fears?", right: "He asked why this wasn't raising fears." }
          ],
          a: "completed",
          successTip: "Negative questions follow the same rules! Didn't → hadn't (Past Perfect). Haven't → hadn't (Past Perfect). Isn't → wasn't (Past Simple). Subject + negative verb, no inversion! ⏪"
        },

        {
          type: "mc",
          q: "Minister: 'Why didn't you raise awareness through official channels?' → He asked why Zara ___ awareness through official channels.",
          options: ["hadn't raised", "didn't raise", "wasn't raising"],
          a: "hadn't raised",
          tip: "Why (WH-) + subject + hadn't raised. Didn't raise (Past Simple negative) → hadn't raised (Past Perfect negative).",
          successTip: "DIDN'T RAISE → HADN'T RAISED (Past Simple negative → Past Perfect negative). WHY + SUBJECT + HADN'T RAISED (no inversion!). RAISE AWARENESS collocation intact! 💡"
        },

        {
          type: "mc",
          q: "Minister: 'Haven't you raised fears about the economy?' → He asked whether Zara ___ fears about the economy.",
          options: ["hadn't raised", "hasn't raised", "didn't raise"],
          a: "hadn't raised",
          tip: "Whether = yes/no question. Haven't raised → hadn't raised (Present Perfect negative → Past Perfect negative).",
          successTip: "HAVEN'T RAISED → HADN'T RAISED (Present Perfect negative → Past Perfect negative). WHETHER (yes/no). RAISE FEARS collocation. Three correct elements! ⚠️"
        },

        {
          type: "mc",
          q: "Minister: 'Why isn't this raising the right questions?' → He asked why this ___ the right questions.",
          options: ["wasn't raising", "isn't raising", "hadn't raised"],
          a: "wasn't raising",
          tip: "Why (WH-) + subject + wasn't raising. Isn't raising → wasn't raising (Present Continuous negative → Past Continuous negative).",
          successTip: "ISN'T RAISING → WASN'T RAISING (Present Continuous negative → Past Continuous negative). WHY + THIS + WASN'T RAISING. RAISE THE QUESTION collocation! ❓"
        },

        {
          type: "mc",
          q: "Choose the correctly reported negative question: Original: 'Why hasn't the campaign raised more eyebrows?'",
          options: [
            "He asked why the campaign hadn't raised more eyebrows.",
            "He asked why hadn't the campaign raised more eyebrows.",
            "He asked why the campaign hasn't raised more eyebrows."
          ],
          a: "He asked why the campaign hadn't raised more eyebrows.",
          tip: "Why + subject (the campaign) + hadn't raised (no inversion, backshifted). Hasn't → hadn't.",
          successTip: "Error detection! 'Why HADN'T the campaign' ❌ (inversion). 'Hasn't raised' ❌ (no backshift). 'Why the campaign HADN'T RAISED' ✅ — subject + verb, Past Perfect negative! 👀"
        },

        {
          type: "mc",
          q: "Zara responded with her own reported question. She wanted to know ___ the minister ___ the question of government funding earlier — and ___ he ___ to raise awareness himself.",
          options: ["why / hadn't raised / why / hadn't tried", "if / didn't raise / whether / tried", "why / raised / if / didn't try"],
          a: "why / hadn't raised / why / hadn't tried",
          tip: "Two negative WH- questions = both why + hadn't. Past Simple negatives → Past Perfect negatives.",
          successTip: "ZARA FIRES BACK! WHY + HADN'T RAISED THE QUESTION + WHY + HADN'T TRIED TO RAISE AWARENESS. Both negative Past Perfect, both WHY, both correct word order. The roles reversed! 🔄"
        },

        {
          type: "mc",
          q: "Tom reported the exchange: 'The minister ___ why Zara ___ official channels. Zara ___ why the minister ___ the issue years earlier.'",
          options: ["asked / hadn't used / demanded to know / hadn't raised", "demanded / didn't use / asked / didn't raise", "wondered / hadn't used / enquired / hadn't raised"],
          a: "asked / hadn't used / demanded to know / asked / hadn't raised",
          tip: "Minister = official question = asked. Hadn't used = Past Perfect negative. Zara = strong counter = demanded to know. Hadn't raised = Past Perfect negative.",
          successTip: "ASKED (minister's question) vs DEMANDED TO KNOW (Zara's counter-challenge). Tone shift revealed by verb choice! Both use Past Perfect negatives. Political theatre! 🎭"
        },

        {
          type: "order",
          q: "Reorder: 'El ministro preguntó por qué la campaña no había creado conciencia a través de los canales gubernamentales establecidos.'",
          words: ["The", "minister", "asked", "why", "the", "campaign", "hadn't", "raised", "awareness", "through", "established", "government", "channels."],
          a: "The minister asked why the campaign hadn't raised awareness through established government channels.",
          extra: ["didn't", "hasn't", "raised"]
        },

        {
          type: "mc",
          q: "Priya's analysis: 'The minister's questions all began with why ___ and whether ___. Zara's counter-questions all began with why ___ and how ___. One asked about the past. The other asked about the future.'",
          options: [
            "the campaign hadn't / it hadn't raised / he hadn't / this would",
            "the campaign didn't / it wasn't / he didn't / this will",
            "the campaign hasn't / it wasn't / he hasn't / this would"
          ],
          a: "the campaign hadn't / it hadn't raised / he hadn't / this would",
          tip: "All past actions = Past Perfect negatives. Future = would (modal backshift).",
          successTip: "Minister (past accusations) = HADN'T + HADN'T RAISED. Zara (future vision) = HADN'T (his failures) + WOULD (the future). Past Perfect negatives + modal backshift. Political linguistics! 🎭📰"
        },

        {
          type: "input",
          q: "Tom's conclusion: 'The most powerful moment was when Zara asked the minister ___ he ___ ___ the question himself — years before the campaign even existed.' (why + not raise — Past Perfect)",
          a: "why / hadn't raised",
          alts: ["Why / hadn't raised"],
          successTip: "WHY + HADN'T RAISED THE QUESTION (Past Perfect negative + raise the question collocation). The ultimate counter-question — using the same collocation the minister had weaponised against her! ❓🏆"
        }
      ]
    },

    // ===================================================================
    // SET 9: QUESTION SEQUENCES (B2+)
    // ===================================================================
    {
      id: "m10-u12-set9",
      title: "Set 9: One Question After Another",
      context: `*Karachi, Sunday — full campaign review meeting*

The whole team had gathered: Zara, Tom, Priya, and Marcus.
They were reviewing every question from the week —
who had asked what, how it had been reported,
and what all the questions together told them about the campaign.
"Questions in sequence," Priya said, "tell a story of their own."

*Karachi, domingo — Reunión completa de revisión de la campaña*

Todo el equipo se había reunido: Zara, Tom, Priya y Marcus.
Revisaban todas las preguntas de la semana —
quién había preguntado qué, cómo se había reportado,
y qué decían todas las preguntas juntas sobre la campaña.
"Las preguntas en secuencia," dijo Priya, "cuentan su propia historia."`,

      questions: [
        {
          type: "mc",
          q: "Tom summarised the week in four reported questions. Which sequence is fully correct?",
          options: [
            "He asked whether the campaign had raised enough money, why it had raised eyebrows, how it had raised awareness, and whether it would raise the roof at the finale.",
            "He asked whether had the campaign raised money, why raised it eyebrows, how had it raised awareness, and whether would it raise the roof.",
            "He asked if the campaign raised money, why it raises eyebrows, how it raised awareness, and if it will raise the roof."
          ],
          a: "He asked whether the campaign had raised enough money, why it had raised eyebrows, how it had raised awareness, and whether it would raise the roof at the finale.",
          tip: "All four collocations + correct word order + correct backshift throughout. Option 1 is the only fully correct sequence.",
          successTip: "ALL FOUR RAISE COLLOCATIONS in one sentence! Whether + had raised money ✅ Why + had raised eyebrows ✅ How + had raised awareness ✅ Whether + would raise the roof ✅ Perfect! 🏆"
        },

        {
          type: "mc",
          q: "Priya had noted the most important questions. She reported: 'The public ___ whether the campaign ___ real change, journalists ___ why it ___ such strong reactions, and officials ___ how it ___ raised so much money so quickly.'",
          options: ["wondered / would bring / asked / had provoked / demanded to know / had", "asked / brought / enquired / raised / wondered / raised", "wondered / would bring / demanded / had provoked / asked / had"],
          a: "wondered / would bring / asked / had provoked / demanded to know / had",
          tip: "Public = internal wondering. Journalists = neutral asking. Officials = aggressive demand. Would bring, had provoked, had raised = backshifts.",
          successTip: "Three groups, three reporting verbs: WONDERED (public reflection) + ASKED (neutral journalism) + DEMANDED TO KNOW (official aggression). The verb reveals the questioner every time! 🎭"
        },

        {
          type: "mc",
          q: "Marcus reported the week's questions as a narrative arc: 'It began with volunteers ___ their hands and asking ___ they ___ commit. It ended with officials demanding to know ___ the campaign ___ raising fears — or solutions.'",
          options: ["raising / whether / could / whether / was", "raising / if / can / if / is", "raised / whether / had / why / had"],
          a: "raising / whether / could / whether / was",
          tip: "Raising hands = gerund in context. Whether/if = yes/no. Could = backshift of can. Whether + was raising = yes/no + Past Continuous backshift.",
          successTip: "RAISING HANDS (collocation as gerund) + WHETHER + COULD (modal backshift) + WHETHER + WAS RAISING FEARS (yes/no + continuous backshift). The full arc of the week! 🙌⚠️"
        },

        {
          type: "mc",
          q: "Zara reflected: 'The question I kept asking myself was ___ we ___ really ___ awareness — or just ___.'",
          options: ["whether / were / raising / attention", "if / raised / raising / noise", "whether / had / raised / eyebrows"],
          a: "whether / were / raising / attention",
          tip: "Internal reflection question = whether. Were raising = past continuous backshift of are raising. Raise awareness vs just raising attention = the honest distinction.",
          successTip: "WHETHER (yes/no — she's questioning herself) + WERE RAISING (backshift) + RAISING AWARENESS (the collocation she was testing). The most honest question of the unit! 💡"
        },

        {
          type: "mc",
          q: "Tom wrote the sequence of questions that defined the week: ministers ___ why Zara ___ official concerns, journalists ___ whether the campaign ___ the roof, and volunteers ___ how they ___ raise money in their communities.",
          options: ["asked / hadn't addressed / wondered / would raise / enquired / could", "demanded / didn't address / asked / raised / wondered / can", "enquired / hadn't raised / asked / would raise / demanded / could"],
          a: "asked / hadn't addressed / wondered / would raise / enquired / could",
          tip: "Ministers = official = asked. Hadn't addressed = Past Perfect negative. Journalists = reflective = wondered. Would raise = backshift. Volunteers = formal/polite = enquired. Could = backshift.",
          successTip: "ASKED + HADN'T (official) + WONDERED + WOULD RAISE THE ROOF (reflective) + ENQUIRED + COULD (polite). Three reporting verbs, three groups, all correct! 🎭💡🎉"
        },

        {
          type: "order",
          q: "Reorder: 'Priya preguntó si la campaña había cumplido su objetivo, por qué había despertado tanto temor, y cómo había recaudado tanto dinero en tan poco tiempo.'",
          words: ["Priya", "asked", "whether", "the", "campaign", "had", "achieved", "its", "goal,", "why", "it", "had", "raised", "so", "much", "fear,", "and", "how", "it", "had", "raised", "so", "much", "money", "in", "such", "a", "short", "time."],
          a: "Priya asked whether the campaign had achieved its goal, why it had raised so much fear, and how it had raised so much money in such a short time.",
          extra: ["if", "did", "raising"]
        },

        {
          type: "mc",
          q: "The team agreed the most revealing question of the week had been ___ anyone ___ asked ___ the campaign ___ raising awareness or just ___ eyebrows.",
          options: ["whether / had ever / whether / was / raising", "if / has / if / is / raised", "whether / had / if / raised / raising"],
          a: "whether / had ever / whether / was / raising",
          tip: "Whether (outer yes/no). Had ever asked = Past Perfect. Whether (inner yes/no). Was raising = Past Continuous backshift. Raising eyebrows = collocation.",
          successTip: "REPORTED QUESTION INSIDE A REPORTED QUESTION! Whether + had ever asked + whether + was raising awareness + raising eyebrows. Two RAISE collocations, two WHETHER questions! 👀💡🏆"
        },

        {
          type: "input",
          q: "Marcus's final note: 'The question nobody ___ asked was not ___ the campaign ___ enough money or ___ it ___ the roof — but ___ it ___ the right question at the right time.' (past perfect + whether + raise + whether + raise the roof + whether + raise the question)",
          a: "had / whether / had raised / whether / would raise / whether / had raised",
          alts: ["Had / whether / had raised / whether / would raise / whether / had raised"],
          successTip: "Three WHETHER questions, three RAISE collocations (raise money + raise the roof + raise the question), all with correct backshift. The most complex sentence in the unit — and you got it! 🏆🎯"
        }
      ]
    },

    // ===================================================================
    // SET 10: FULL NARRATIVE — ALL STRUCTURES (B2+)
    // ===================================================================
    {
      id: "m10-u12-set10",
      title: "Set 10: The Questions That Mattered",
      context: `*Karachi, the final event — campaign closing ceremony*

The campaign had run for thirty days.
It had raised money, raised awareness, raised eyebrows, raised fears,
raised the question of accountability, raised hands across twelve countries —
and at the closing ceremony, it raised the roof.
Now every question asked during the month needed to be reported.
Some of them, as Priya would write, had changed everything.

*Karachi, el evento final — ceremonia de cierre de la campaña*

La campaña había durado treinta días.
Había recaudado dinero, creado conciencia, causado asombro, despertado temores,
planteado la pregunta de la responsabilidad, levantado manos en doce países —
y en la ceremonia de cierre, había puesto el techo abajo.
Ahora había que reportar todas las preguntas formuladas durante el mes.
Algunas de ellas, como escribiría Priya, habían cambiado todo.`,

      questions: [
        {
          type: "mc",
          q: "The opening question of the month had been: 'Will this campaign raise awareness globally?' → Priya reported it as: Thirty days earlier, Tom had wondered ___.",
          options: [
            "whether the campaign would raise awareness globally.",
            "whether would the campaign raise awareness globally.",
            "if the campaign will raise awareness globally."
          ],
          a: "whether the campaign would raise awareness globally.",
          tip: "Whether (yes/no formal). Would raise = backshift of will raise. Subject before verb — no inversion.",
          successTip: "WHETHER + THE CAMPAIGN (subject) + WOULD RAISE (verb — no inversion!) + AWARENESS (collocation). The first question of the month, reported perfectly thirty days later! 💡"
        },

        {
          type: "mc",
          q: "The question that raised eyebrows had been: 'How is a 29-year-old activist raising more money than the government?' → Marcus reported: Everyone wondered ___.",
          options: [
            "how a 29-year-old activist was raising more money than the government.",
            "how was a 29-year-old activist raising more money than the government.",
            "how a 29-year-old activist raised more money than the government."
          ],
          a: "how a 29-year-old activist was raising more money than the government.",
          tip: "How + subject + was raising. Is raising → was raising (backshift). No inversion after how.",
          successTip: "HOW + SUBJECT + WAS RAISING (no inversion!). IS RAISING → WAS RAISING (backshift). RAISE MONEY collocation. The question that raised eyebrows, reported perfectly! 👀💰"
        },

        {
          type: "mc",
          q: "The question that raised fears had been: 'Will this raise fears about government incompetence?' → The minister's team wanted to know ___.",
          options: [
            "whether this would raise fears about government incompetence.",
            "if this will raise fears about government incompetence.",
            "whether would this raise fears about government incompetence."
          ],
          a: "whether this would raise fears about government incompetence.",
          tip: "Whether (formal yes/no). Would = backshift of will. This + would raise = correct word order.",
          successTip: "WHETHER + THIS + WOULD RAISE FEARS (no inversion!). WILL → WOULD (backshift). RAISE FEARS collocation. The question that scared the government, reported accurately! ⚠️"
        },

        {
          type: "mc",
          q: "The question nobody had answered was: 'Why didn't officials raise the question of climate funding years ago?' → Priya wrote that this ___.",
          options: [
            "was the question everyone had wondered why officials hadn't raised years ago.",
            "was the question everyone had wondered why hadn't officials raised years ago.",
            "was the question everyone wondered why officials didn't raise years ago."
          ],
          a: "was the question everyone had wondered why officials hadn't raised years ago.",
          tip: "Why + officials (subject) + hadn't raised (Past Perfect negative). Had wondered = Past Perfect. No inversion.",
          successTip: "Reported question inside a narrative! WHY + OFFICIALS + HADN'T RAISED (Past Perfect negative, no inversion). HAD WONDERED (Past Perfect for the wondering itself). RAISE THE QUESTION collocation! ❓"
        },

        {
          type: "mc",
          q: "At the closing event: 'Would the finale raise the roof?' → In her article, Priya wrote: ___ the finale ___ the roof had been the last question anyone ___.",
          options: ["Whether / would raise / asked", "If / will raise / had asked", "Whether / raised / wondered"],
          a: "Whether / would raise / asked",
          tip: "Whether (yes/no). Would raise = backshift of will raise. Asked = simple past reporting.",
          successTip: "WHETHER + WOULD RAISE THE ROOF (yes/no + modal backshift + collocation). The final question before the finale — reported in full! And yes — it did raise the roof! 🎉"
        },

        {
          type: "mc",
          q: "Zara was asked at the end: 'What question did this campaign raise that you hadn't expected?' → She replied that ___ the question ___ hadn't expected ___ a simple one: why ___ it taken so long for the world ___ pay attention.",
          options: ["the campaign / she / was / had / to", "which / she / was / did / to", "the question / they / were / had / to"],
          a: "the campaign / she / was / had / to",
          tip: "She explained: the campaign raised a question = she hadn't expected = was (backshift of is). Why had it taken = Past Perfect. To pay = infinitive.",
          successTip: "THE QUESTION THE CAMPAIGN RAISED + SHE HADN'T EXPECTED + WAS + WHY + HAD TAKEN. Complex embedded reported answer with raise the question + Past Perfect + backshift! 🔬🏆"
        },

        {
          type: "mc",
          q: "Marcus wrote the closing paragraph using all seven raise collocations. Choose the fully correct version:",
          options: [
            "The campaign raised awareness, raised money, raised eyebrows, raised fears, raised hands, raised the question — and at the very end, raised the roof.",
            "The campaign risen awareness, raised money, rose eyebrows, raised fears, lifted hands, raised the question — and raised the roof.",
            "The campaign has raised awareness, raised money, raised eyebrows, raises fears, raised hands, raised the question — and raised the roof."
          ],
          a: "The campaign raised awareness, raised money, raised eyebrows, raised fears, raised hands, raised the question — and at the very end, raised the roof.",
          tip: "All seven collocations with raise in Past Simple. No risen, no rose, no lifts — all RAISED!",
          successTip: "ALL SEVEN RAISE COLLOCATIONS in one sentence! Raised awareness + money + eyebrows + fears + hands + the question + the roof. Past Simple throughout. The complete collocation set! 🏆🎉"
        },

        {
          type: "order",
          q: "Reorder: 'Priya preguntó si la campaña había planteado las preguntas correctas, por qué había despertado tanto temor, y cómo había conseguido al mismo tiempo levantar el techo.'",
          words: ["Priya", "asked", "whether", "the", "campaign", "had", "raised", "the", "right", "questions,", "why", "it", "had", "raised", "so", "much", "fear,", "and", "how", "it", "had", "managed", "to", "raise", "the", "roof", "at", "the", "same", "time."],
          a: "Priya asked whether the campaign had raised the right questions, why it had raised so much fear, and how it had managed to raise the roof at the same time.",
          extra: ["if", "did", "raising"]
        },

        {
          type: "mc",
          q: "Tom's final editorial question to himself: '___ the campaign ___ real change or just ___ the question ___ real change was possible?' He ___ he ___ the answer — but the numbers ___ raising eyebrows even now.",
          options: ["Whether / had raised / raised / whether / admitted / didn't know / were", "If / raised / asking / if / said / knew / are", "Whether / would raise / raised / if / explained / knew / had been"],
          a: "Whether / had raised / raised / whether / admitted / didn't know / were",
          tip: "Whether (yes/no). Had raised (Past Perfect). Raised the question. Whether (embedded yes/no). Admitted (Unit 10 verb!). Didn't know (Past Simple). Were raising (Past Continuous backshift).",
          successTip: "Unit 10 and Unit 12 combined! WHETHER + HAD RAISED + RAISE THE QUESTION + WHETHER + ADMITTED (reporting verb from Unit 10!) + WERE RAISING EYEBROWS. Two units, one perfect sentence! 🔗🏆"
        },

        {
          type: "input",
          q: "Priya's final line: 'The campaign had ___ awareness, ___ fears, ___ eyebrows, ___ hands, ___ money, ___ the question — and finally, ___ the roof. The only question left was ___ it had raised enough.' (seven raise collocations + whether — all past simple)",
          a: "raised / raised / raised / raised / raised / raised / raised / whether",
          alts: ["Raised / raised / raised / raised / raised / raised / raised / whether"],
          successTip: "🏆 UNIT 12 COMPLETE! All seven RAISE collocations ✅ Yes/No reported questions (if/whether) ✅ WH- reported questions ✅ Correct word order (no inversion!) ✅ Full backshift ✅ Multiple reporting verbs ✅ Negative reported questions ✅ You report questions like a professional journalist! 📰🌍"
        }
      ]
    }
  ],

  // ===================================================================
  // READING SET (B2)
  // ===================================================================
  readingSet: {
    id: "m10-u12-reading",
    title: "Reading: The Questions That Shaped the Story",
    category: "reading",

    questions: [

      // --- PASSAGE 1 ---
      {
        type: "passage",
        passage: `**The First Interview**

Tom asked whether Zara had raised enough money to run the campaign for a full month. She said she had. He then asked how the campaign was raising awareness in areas with limited internet access. She explained that volunteers were distributing printed materials in rural communities.`
      },
      {
        type: "mc",
        q: "What did Tom ask first?",
        options: ["Whether Zara had raised enough money", "How the campaign was raising awareness", "Why the campaign had raised fears"],
        a: "Whether Zara had raised enough money",
        tip: "'Tom asked whether Zara had raised enough money...'"
      },
      {
        type: "mc",
        q: "How was the campaign raising awareness in areas with limited internet?",
        options: ["Through printed materials distributed by volunteers", "Through social media campaigns", "Through government channels"],
        a: "Through printed materials distributed by volunteers",
        tip: "'...volunteers were distributing printed materials in rural communities.'"
      },

      // --- PASSAGE 2 ---
      {
        type: "passage",
        passage: `**The Eyebrows Question**

Priya wondered why the campaign had raised so many eyebrows among established NGOs. Zara admitted that her unconventional methods had surprised many people. She added that raising eyebrows was sometimes necessary to raise awareness.`
      },
      {
        type: "mc",
        q: "What did Priya wonder?",
        options: ["Why the campaign had raised eyebrows among NGOs", "Whether the campaign had raised enough money", "How the campaign had raised fears"],
        a: "Why the campaign had raised eyebrows among NGOs",
        tip: "'Priya wondered why the campaign had raised so many eyebrows...'"
      },
      {
        type: "mc",
        q: "What did Zara say about raising eyebrows?",
        options: ["It was sometimes necessary to raise awareness", "It had damaged the campaign's reputation", "It had raised fears among officials"],
        a: "It was sometimes necessary to raise awareness",
        tip: "'...raising eyebrows was sometimes necessary to raise awareness.'"
      },

      // --- PASSAGE 3 ---
      {
        type: "passage",
        passage: `**The Minister's Challenge**

The minister demanded to know why the campaign hadn't raised awareness through official government channels. He also asked whether it had raised fears about policy failures. Zara replied that she had raised the question many times through official channels — and received no response.`
      },
      {
        type: "mc",
        q: "What did the minister demand to know?",
        options: ["Why the campaign hadn't used official channels", "Whether the campaign had raised enough money", "How the campaign had raised the roof"],
        a: "Why the campaign hadn't used official channels",
        tip: "'The minister demanded to know why the campaign hadn't raised awareness through official government channels.'"
      },
      {
        type: "mc",
        q: "What had Zara done many times through official channels?",
        options: ["Raised the question", "Raised money", "Raised awareness"],
        a: "Raised the question",
        tip: "'...she had raised the question many times through official channels.'"
      },

      // --- PASSAGE 4 ---
      {
        type: "passage",
        passage: `**The Closing Event**

Tom wondered whether the finale would raise the roof. It did. Thousands of volunteers raised their hands when asked whether they would continue the work. Marcus enquired how the campaign had managed to raise so much money in just thirty days. Nobody had a simple answer.`
      },
      {
        type: "mc",
        q: "What did Tom wonder about the finale?",
        options: ["Whether it would raise the roof", "Whether it had raised enough money", "Why it had raised fears"],
        a: "Whether it would raise the roof",
        tip: "'Tom wondered whether the finale would raise the roof.'"
      },
      {
        type: "mc",
        q: "What did Marcus enquire about?",
        options: ["How the campaign had raised so much money in thirty days", "Whether volunteers had raised their hands", "Why the campaign had raised eyebrows"],
        a: "How the campaign had raised so much money in thirty days",
        tip: "'Marcus enquired how the campaign had managed to raise so much money in just thirty days.'"
      },

      // --- PASSAGE 5 ---
      {
        type: "passage",
        passage: `**Priya's Final Article**

Priya wrote that the campaign had raised awareness, raised money, raised eyebrows and raised the question of political accountability. She asked whether any campaign had ever raised all seven questions at once. Her editor said that was itself a question worth raising.`
      },
      {
        type: "mc",
        q: "According to Priya, what had the campaign raised?",
        options: ["Awareness, money, eyebrows and the question of accountability", "Fears, hands, the roof and money", "Awareness, fears, eyebrows and the roof"],
        a: "Awareness, money, eyebrows and the question of accountability",
        tip: "'...raised awareness, raised money, raised eyebrows and raised the question of political accountability.'"
      },
        {
        type: "mc",
        q: "What did Priya's editor say about her final question?",
        options: [
          "That it was itself a question worth raising.",
          "That it had already been answered.",
          "That it raised too many fears."
        ],
        a: "That it was itself a question worth raising.",
        tip: "'Her editor said that was itself a question worth raising.'",
        successTip: "RAISE THE QUESTION — used one final time, by the editor himself. The collocation bookends the whole unit! ❓🏆"
      }
    ]
  },

  // ===================================================================
  // REVIEW SET (B2+)
  // ===================================================================
  reviewSet: {
    id: "m10-u12-review",
    title: "Unit 12 Review: Reported Questions + Raise Collocations",
    category: "review",

    questions: [
      {
        type: "mc",
        q: "Review: Tom asked ___ the campaign ___ enough money to continue.",
        options: ["whether / had raised", "if / has raised", "whether / raised"],
        a: "whether / had raised",
        tip: "Yes/No question → whether. Has raised → had raised (backshift)."
      },
      {
        type: "mc",
        q: "Review: She asked ___ the campaign ___ awareness in rural areas.",
        options: ["how / was raising", "how / is raising", "what / was raising"],
        a: "how / was raising",
        tip: "WH- question (how) + subject + verb. Is raising → was raising (backshift)."
      },
      {
        type: "mc",
        q: "Review: He wondered ___ the finale ___ the roof.",
        options: ["whether / would raise", "if / will raise", "whether / raised"],
        a: "whether / would raise",
        tip: "Internal reflection = wondered. Whether = yes/no. Will → would (backshift)."
      },
      {
        type: "mc",
        q: "Review: Which sentence has the correct word order?",
        options: [
          "She asked why the campaign had raised so many fears.",
          "She asked why had the campaign raised so many fears.",
          "She asked why did the campaign raise so many fears."
        ],
        a: "She asked why the campaign had raised so many fears.",
        tip: "Why + subject (the campaign) + verb (had raised). No inversion, no auxiliary."
      },
      {
        type: "mc",
        q: "Review: The minister demanded to know ___ Zara ___ the question through official channels.",
        options: ["why / hadn't raised", "why / didn't raise", "whether / hadn't raised"],
        a: "why / hadn't raised",
        tip: "Why (WH-). Didn't raise → hadn't raised (Past Simple negative → Past Perfect negative)."
      },
      {
        type: "mc",
        q: "Review: Volunteers raised their hands when asked ___ they ___ committed to the project.",
        options: ["whether / were", "if / are", "whether / had been"],
        a: "whether / were",
        tip: "Yes/No embedded question = whether. Are → were (backshift)."
      },
      {
        type: "mc",
        q: "Review: She ___ how the campaign ___ eyebrows without raising fears at the same time.",
        options: ["enquired / had raised", "demanded / raised", "wondered / raises"],
        a: "enquired / had raised",
        tip: "Polite/formal question = enquired. Has raised → had raised (backshift). Raise eyebrows = collocation."
      },
      {
        type: "mc",
        q: "Review: Match the reporting verb to its tone — aggressive, polite, neutral, internal:",
        options: [
          "demanded to know / enquired / asked / wondered",
          "enquired / demanded / wondered / asked",
          "asked / wondered / enquired / demanded"
        ],
        a: "demanded to know / enquired / asked / wondered",
        tip: "Demanded = aggressive. Enquired = polite/formal. Asked = neutral. Wondered = internal/reflective."
      },
      {
        type: "mc",
        q: "Review: Priya wrote that the campaign ___ awareness, ___ money, ___ eyebrows and ___ the question of accountability.",
        options: ["had raised / had raised / had raised / had raised", "raised / raised / raised / raised", "has raised / raised / had raised / raised"],
        a: "raised / raised / raised / raised",
        tip: "Past Simple narrative reporting. All four raise collocations in parallel structure.",
        successTip: "All four RAISE collocations in Past Simple narrative! Raised awareness + money + eyebrows + the question. Four collocations, one sentence, consistent tense! 💡💰👀❓"
      },
      {
        type: "mc",
        q: "Review: Tom's final question was ___ the campaign ___ raised the right questions — or just the right amount of noise.",
        options: ["whether / had", "if / has", "whether / raised"],
        a: "whether / had",
        tip: "Whether (yes/no formal). Had raised = Past Perfect backshift. Raise the question = collocation.",
        successTip: "🎉 UNIT 12 COMPLETE! Yes/No reported questions ✅ WH- reported questions ✅ No inversion ✅ Full backshift ✅ Negative reported questions ✅ Reporting verbs for questions ✅ All 7 raise collocations ✅ You ask — and report — like a professional! 📰🌍🏆"
      }
    ]
  }
};
